import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import {
  LocalStorageKey,
  OLD_APP_ID,
  SessionStorageKey,
} from "@/constants/constants";
import { NetLogin } from "@/constants/netEvent";
import { LoginTypeEnum } from "@/enum/loginRegister";
import { useAfterLogin } from "@/hooks/login/useAfterLogin";
import { useUrlStorage } from "@/hooks/useUrlStorage";
import { aesEncryptWithCommonKey } from "@/lib/aesUtils";
import { clearToken } from "@/lib/apiUtils";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@/lib/localStorage";
import { getImgurl } from "@/lib/resFolder";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { ThinkingDataSDK } from "@/lib/sdk/thinkingData";
import { SDKName } from "@/lib/sdk/types";
import { removeSessionStorageItem } from "@/lib/sessionStorage.ts";
import { convertObjectToStringRecord, getCurrentVersion } from "@/lib/utils";
import { reqPost } from "@/service/clientRequest";
import { clearBonusCenterStore } from "@/stores/bonusCenterStore";
import { configStore } from "@/stores/configStore";
import { deviceInfo, getDeviceObject } from "@/stores/deviceStore";
import {
  getAdParams,
  getAdReferer,
  getAppPackageInfoParams,
  sysConfigStore,
} from "@/stores/sysConfigStore.ts";
import {
  clearUserStore,
  getUserInfo,
  upDateUserStore,
} from "@/stores/userStore";
import { closeWebSocket } from "@/stores/websocketStore";
import {
  CheckEnabledRegisterValidateCodeReq,
  CheckEnabledRegisterValidateCodeResp,
  CheckUserAccExistsReq,
  CheckUserAccExistsResp,
  ForgetPwdByCPFReq,
  ForgetPwdByCPFResp,
  GetPhoneVerifyCodeReq,
  GetPhoneVerifyCodeResp,
  LoginV2Resp,
  LoginV3ReqExtra,
  LogoutReq,
  LogoutResp,
  NewForgetCodeCheckReq,
  NewForgetCodeCheckRsp,
  NewForgetPwdReq,
  RegisterV3Req,
  RegisterV3ReqExtra,
  UserAccountLoginV3Req,
  UserGoogleLoginV3Req,
  UserTokenLoginV3Req,
  VerifyCodeEmailReq,
  VerifyRegCodeReq,
  VerifyRegCodeResp,
} from "@/types/swagger/loginV2";
import { UserLoginByFaceIndexReq } from "@/types/swaggerex/activity";

//账号登录——走login进程
export const useLoginByAccountStandard = async (params: {
  account: string;
  password: string;
  rememberMe: boolean;
  a_code?: string;
  loginType: string;
}) => {
  const { getStoredParams } = useUrlStorage();
  const urlParams = getStoredParams().params;

  const extra: LoginV3ReqExtra = {
    pack: configStore.configVersion?.pack_no || 0,
    device: getDeviceObject(true),
    invite_code: urlParams?.ic || "",
    first_map: "", // 首次打开url地址
    source_map: "",
    advertise_type: 0, // TODO advertise_type
    advertise_id: "", // TODO advertise_id
    advertise_extra: "", // TODO advertise_extra
  };
  const { default: SparkMD5 } = await import("@repo/sparkmd5");
  const md5Pwd = SparkMD5.hash(params.password || "");
  const loinParams: UserAccountLoginV3Req = {
    phone: "",
    password: md5Pwd,
    extra: extra,
    email: "",
    account: params.account,
    a_code: "",
    old_app_id: OLD_APP_ID || 0,
  };

  // if (params.loginType === LoginTypeEnum.cpf) {
  //   loinParams.cpf = params.username;
  // } else if (params.loginType === LoginTypeEnum.email) {
  //   loinParams.email = params.username;
  // } else {
  //   loinParams.phone = params.username;
  // }

  if (params.rememberMe) {
    setLocalStorageItem(
      LocalStorageKey.REMEMBER_ME,
      aesEncryptWithCommonKey(JSON.stringify(params)),
    );
  } else {
    removeLocalStorageItem(LocalStorageKey.REMEMBER_ME);
  }

  await loginStandard(loinParams);
  await getUserInfo(true);

  return true;
};

//账号登录——走login进程
export const useLoginByAccount = async (params: {
  username: string;
  password: string;
  rememberMe: boolean;
  a_code?: string;
  loginType: string;
}) => {
  const { getStoredParams } = useUrlStorage();
  const urlParams = getStoredParams().params;

  const extra: LoginV3ReqExtra = {
    pack: configStore.configVersion?.pack_no || 0,
    device: getDeviceObject(true),
    invite_code: urlParams?.ic || "",
    first_map: "", // 首次打开url地址
    source_map: "",
    advertise_type: 0, // TODO advertise_type
    advertise_id: "", // TODO advertise_id
    advertise_extra: "", // TODO advertise_extra
  };
  const { default: SparkMD5 } = await import("@repo/sparkmd5");
  const md5Pwd = SparkMD5.hash(params.password || "");
  const loinParams: UserAccountLoginV3Req = {
    phone: "",
    password: md5Pwd,
    extra: extra,
    email: "",
    account: "",
    a_code: "",
    old_app_id: OLD_APP_ID || 0,
    cpf: "",
  };

  if (params.loginType === LoginTypeEnum.cpf) {
    loinParams.cpf = params.username;
  } else if (params.loginType === LoginTypeEnum.email) {
    loinParams.email = params.username;
  } else {
    loinParams.phone = params.username;
  }

  if (params.rememberMe) {
    setLocalStorageItem(
      LocalStorageKey.REMEMBER_ME,
      aesEncryptWithCommonKey(JSON.stringify(params)),
    );
  } else {
    removeLocalStorageItem(LocalStorageKey.REMEMBER_ME);
  }

  await login(loinParams);

  await getUserInfo(true);

  return true;
};

//自动登录——走login进程
export const useLoginByAuto = async (params: {
  uid: number;
  token: string;
  new_login: boolean;
  type?: number;
}) => {
  const { getStoredParams } = useUrlStorage();
  const urlParams = getStoredParams().params;

  const extra: LoginV3ReqExtra = {
    pack: configStore.configVersion?.pack_no || 0,
    device: getDeviceObject(true),
    invite_code: urlParams?.ic || "",
    first_map: "", // 首次打开url地址
    source_map: "",
    advertise_type: 0, // TODO advertise_type
    advertise_id: "", // TODO advertise_id
    advertise_extra: "", // TODO advertise_extra
  };
  const loinParams: UserTokenLoginV3Req = {
    token: params.token,
    new_login: params.new_login,
    extra: extra,
  };

  await autoTokenLogin(loinParams);

  // await getUserInfo(true);

  return true;
};

export const useLoginByGoogle = async (credential: string) => {
  const { saveCookieParams, getStoredParams, params } = useUrlStorage();
  saveCookieParams();
  const fbInitData: Record<string, string> = getLocalStorageItem(
    LocalStorageKey.FacebookInit,
    {},
  );
  const twaInitData: Record<string, string> = getLocalStorageItem(
    LocalStorageKey.TwaInit,
    {},
  );
  const rawConfigVersionHistory = getLocalStorageItem(
    LocalStorageKey.ConfigVersionHistory,
    {} as Record<string, unknown>,
  );

  // // 重新组装成 Record<string, string> 格式，以防万一数据格式不一致
  // const configVersionHistory: Record<string, string> = {};
  // Object.entries(rawConfigVersionHistory).forEach(([key, value]) => {
  //   if (typeof value === "string") {
  //     configVersionHistory[key] = value;
  //   } else {
  //     // 如果不是字符串，则序列化为字符串
  //     configVersionHistory[key] = JSON.stringify(value);
  //   }
  // });

  const extra: RegisterV3ReqExtra = {
    pack: configStore.configVersion?.pack_no || 0,
    device: deviceInfo,
    first_map: "", // 首次打开url地址
    source_map: "",
    advertise_type: 0, // TODO advertise_type
    advertise_id: "", // TODO advertise_id
    advertise_extra: "",
    is_experience_point: false,
    advertise_src: "",
    advertise_group: "",
    advertise_affiliate_id: "",
    google_play_integrity_token: "",
    visit_url: getStoredParams()?.url || params?.url || window.location.href,
    client_version: getCurrentVersion() || "",
    url_referers: getStoredParams()?.reffer || [],
    ad_referer: getAdReferer(),
    app_package_info: getAppPackageInfoParams(),
    client_params: convertObjectToStringRecord({
      ...getAdParams(),
      ...fbInitData,
      ...twaInitData,
      // ...configVersionHistory,
    }),
  };

  const googleLoginParams: UserGoogleLoginV3Req = {
    code: credential,
    invite_code: getStoredParams()?.params?.ic || "",
    extra: extra,
    old_app_id: OLD_APP_ID || 0,
  };

  const res = await reqPost<UserGoogleLoginV3Req, LoginV2Resp>(
    googleLoginParams,
    NetLogin.mod,
    NetLogin.UserGoogleLoginV3,
    false,
  );
  if (res) {
    const { afterLoginSuccess } = useAfterLogin();
    afterLoginSuccess();
    useDialog().closeDialog(DialogType.RetainPopup);
    removeSessionStorageItem(SessionStorageKey.Buoy);
    upDateUserStore(res);
  }
};

export const useLoginByFaceIndex = async (action_id: string) => {
  const { saveCookieParams, getStoredParams, params } = useUrlStorage();
  saveCookieParams();
  const fbInitData: Record<string, string> = getLocalStorageItem(
    LocalStorageKey.FacebookInit,
    {},
  );
  const twaInitData: Record<string, string> = getLocalStorageItem(
    LocalStorageKey.TwaInit,
    {},
  );
  const rawConfigVersionHistory2 = getLocalStorageItem(
    LocalStorageKey.ConfigVersionHistory,
    {} as Record<string, unknown>,
  );

  // 重新组装成 Record<string, string> 格式，以防万一数据格式不一致
  const configVersionHistory: Record<string, string> = {};
  Object.entries(rawConfigVersionHistory2).forEach(([key, value]) => {
    if (typeof value === "string") {
      configVersionHistory[key] = value;
    } else {
      // 如果不是字符串，则序列化为字符串
      configVersionHistory[key] = JSON.stringify(value);
    }
  });

  const extra: RegisterV3ReqExtra = {
    pack: configStore.configVersion?.pack_no || 0,
    device: deviceInfo,
    first_map: "", // 首次打开url地址
    source_map: "",
    advertise_type: 0, // TODO advertise_type
    advertise_id: "", // TODO advertise_id
    advertise_extra: "",
    is_experience_point: false,
    advertise_src: "",
    advertise_group: "",
    advertise_affiliate_id: "",
    google_play_integrity_token: "",
    visit_url: getStoredParams()?.url || params?.url || window.location.href,
    client_version: getCurrentVersion() || "",
    url_referers: getStoredParams()?.reffer || [],
    ad_referer: getAdReferer(),
    app_package_info: getAppPackageInfoParams(),
    client_params: convertObjectToStringRecord({
      ...getAdParams(),
      ...fbInitData,
      ...twaInitData,
      // ...configVersionHistory,
    }),
  };

  const googleLoginParams: UserLoginByFaceIndexReq = {
    action_id: action_id,
    invite_code: {},
    extra: extra,
  };

  const res = await reqPost<UserLoginByFaceIndexReq, LoginV2Resp>(
    googleLoginParams,
    NetLogin.mod,
    NetLogin.UserLoginByFaceIndex,
    false,
  );
  if (res) {
    const { afterLoginSuccess } = useAfterLogin();

    afterLoginSuccess();
    removeSessionStorageItem(SessionStorageKey.Buoy);
    upDateUserStore(res);
    window.location.href = "/";
  }
};

const postLogin = (data: UserAccountLoginV3Req) => {
  return reqPost<UserAccountLoginV3Req, LoginV2Resp>(
    data,
    NetLogin.mod,
    NetLogin.UserAccountLoginV3,
    false,
  );
};
const postLoginStandard = (data: UserAccountLoginV3Req) => {
  return reqPost<UserAccountLoginV3Req, LoginV2Resp>(
    data,
    NetLogin.mod,
    NetLogin.UserStandardLogin,
    false,
  );
};

const postAutoLogin = (data: UserTokenLoginV3Req) => {
  return reqPost<UserTokenLoginV3Req, LoginV2Resp>(
    data,
    NetLogin.mod,
    NetLogin.UserTokenLoginV3,
    false,
  );
};

async function login(loinParams: UserAccountLoginV3Req) {
  const res = await postLogin(loinParams);
  if (res) {
    upDateUserStore(res);
    removeSessionStorageItem(SessionStorageKey.Buoy);
  }
}

async function loginStandard(loinParams: UserAccountLoginV3Req) {
  const res = await postLoginStandard(loinParams);
  if (res) {
    upDateUserStore(res);
    removeSessionStorageItem(SessionStorageKey.Buoy);
  }
}

async function autoTokenLogin(loinParams: UserTokenLoginV3Req) {
  const res = await postAutoLogin(loinParams);
  if (res) {
    upDateUserStore(res);
    removeSessionStorageItem(SessionStorageKey.Buoy);
  }
}

export async function postLoginByFaceIndex(
  loinParams: UserLoginByFaceIndexReq,
) {
  const res = await reqPost<UserLoginByFaceIndexReq, LoginV2Resp>(
    loinParams,
    NetLogin.mod,
    NetLogin.UserLoginByFaceIndex,
  );
  if (res) {
    upDateUserStore(res);
    removeSessionStorageItem(SessionStorageKey.Buoy);
  }
}

/** 登出 */
export async function useLoginOut() {
  const res = await reqPost<LogoutReq, LogoutResp>(
    {},
    NetLogin.mod,
    NetLogin.Logout,
    true,
    undefined,
    undefined,
  );
  if (res) {
    sdkManager.tdReportEvent("logout", {});
    closeWebSocket();
    clearUserStore();
    clearToken();
    clearBonusCenterStore();
    sdkManager.getSDK<ThinkingDataSDK>(SDKName.ThinkingDataSDK)?.logout();
    removeSessionStorageItem(SessionStorageKey.Buoy);
  }
  return res;
}

export function useUserLoginByFaceIndex() {
  return reqPost<{}, LoginV2Resp>(
    {},
    NetLogin.mod,
    NetLogin.UserLoginByFaceIndex,
  );
}

/** 检查用户账号是否存在 */
export const CheckUserAccExists = async (req: CheckUserAccExistsReq) => {
  const res = await reqPost<CheckUserAccExistsReq, CheckUserAccExistsResp>(
    req,
    NetLogin.mod,
    NetLogin.CheckUserAccExists,
  );
  return res;
};

/** 获取验证码 */
export const GetPhoneVerifyCode = async (
  req: GetPhoneVerifyCodeReq,
  isLogin: boolean = false,
) => {
  const res = await reqPost<GetPhoneVerifyCodeReq, GetPhoneVerifyCodeResp>(
    req,
    NetLogin.mod,
    NetLogin.GetPhoneVerifyCode,
    isLogin, // 別刪
  );
  return res;
};

//验证码检查
export const VerifyCodeNewPassword = async (req: NewForgetCodeCheckReq) => {
  const res = await reqPost<NewForgetCodeCheckReq, NewForgetCodeCheckRsp>(
    req,
    NetLogin.mod,
    NetLogin.NewForgetCodeCheck,
  );
  return res;
};

//新密码提交
export const NewForgetCodeSubmit = async (req: NewForgetPwdReq) => {
  const res = await reqPost<NewForgetPwdReq, NewForgetCodeCheckRsp>(
    req,
    NetLogin.mod,
    NetLogin.NewForgetAccountPwd,
  );
  return res;
};

/** 账号注册 * */
export const useRegister = async (params: RegisterV3Req) => {
  const res = await reqPost<RegisterV3Req, LoginV2Resp>(
    params,
    NetLogin.mod,
    NetLogin.UserRegisterV3,
  );
  return res;
};

//注册验证码校验
export const VerifyRegisterCode = async (req: VerifyRegCodeReq) => {
  const res = await reqPost<VerifyRegCodeReq, VerifyRegCodeResp>(
    req,
    NetLogin.mod,
    NetLogin.VerifyRegCode,
  );
  return res;
};

//邮箱忘记密码:发带链接的邮件到用户邮箱
export const useVerifyCodeEmail = async (req: VerifyCodeEmailReq) => {
  const pathname = window.location.pathname;
  const pathArr = pathname.split("/");
  const lang = pathArr[1] ? "/" + pathArr[1] : "";
  req.device = getDeviceObject(true);
  req.url = window.location.host + lang;
  req.logo =
    window.location.protocol +
    "//" +
    window.location.host +
    "/" +
    __APP_NAME__ +
    "/images/logo/logo.png"; //getImgurl("logo-h.png", "logo");
  req.host = sysConfigStore.app_name;
  return await reqPost<VerifyCodeEmailReq, VerifyCodeEmailResp>(
    req,
    NetLogin.mod,
    NetLogin.VerifyCodeEmail,
  );
};

// cpf重置密码
export const ForgetPwdByCPF = async (req: ForgetPwdByCPFReq) => {
  const res = await reqPost<ForgetPwdByCPFReq, ForgetPwdByCPFResp>(
    req,
    NetLogin.mod,
    NetLogin.ForgetPwdByCPF,
    false,
  );
  return res;
};

// 检查是否启用注册验证码
export const CheckEnabledRegisterValidateCode = async (
  req: CheckEnabledRegisterValidateCodeReq,
) => {
  const res = await reqPost<
    CheckEnabledRegisterValidateCodeReq,
    CheckEnabledRegisterValidateCodeResp
  >(req, NetLogin.mod, NetLogin.CheckEnabledRegisterValidateCode, false);
  return res;
};
